import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import { isEmpty, isNull } from 'lodash-es';
import {
  AnalysisTypeEnum,
  AppointmentTypeEnum,
  CallDirectionEnum,
  type CallEditableFields,
  type CallRecord,
  getMockCallEditHistory,
  SchedulingOpportunityEnum,
  SchedulingOutcomeEnum,
  SchedulingOutcomeV2Enum,
  useEditAiOutput,
  useGetCallEditHistory,
} from '@frontend/api-call-intel';
import { getUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, Modal, ModalControlModalProps, SkeletonLoaders, Text, useAlert } from '@frontend/design-system';
import { useHasCallIntelAclAccess } from '../../hooks/use-has-call-intel-acl-access';
import { useOptionsProvider } from '../../hooks/use-options-provider';
import { DEFAULT_FIELD_EMPTY_VALUE } from '../../utils/call-analysis-util';
import { callIntelUtils, diffValues } from '../../utils/call-intel-utils';
import { FIELD_MAPPING } from '../../utils/constant';
import { generateTrackingId } from '../../utils/tracking';
import { CallAnalyzeStatus } from '../call-analyze-status';
import { SchedulingChip } from '../chip/scheduling-chip';
import { ContactUserName } from '../user-name/contact-user-name';
import { RestoreHistoryModal } from './restore-history-modal';

const mapEditableFieldsToCall = (editableFields: CallEditableFields): Partial<CallRecord> => {
  const {
    contactType,
    callSentiment,
    schedulingOutcome,
    schedulingOpportunity,
    categories,
    appointmentTypes,
    serviceQualityFlags,
    schedulingOutcomeV2,
  } = editableFields;
  return {
    contactType,
    callSentiment: callSentiment ?? undefined,
    callSchedulingOutcome: schedulingOutcome ?? undefined,
    callSchedulingOpportunity: schedulingOpportunity ?? undefined,
    categories,
    appointmentTypes: appointmentTypes === null ? [] : appointmentTypes?.appointmentType,
    serviceQualityFlags: serviceQualityFlags?.map((flag) => flag.serviceQualityFlag),
    schedulingOutcomeV2,
  };
};

type EditHistoryModalProps = {
  isFailedCall?: boolean;
  isPoorAudio?: boolean;
  isSkippedCall?: boolean;
  modalProps: ModalControlModalProps;
  onClose: () => void;
  updateEditedCallInList: (updatedFields: Partial<CallRecord>, isRestore?: boolean) => void;
  call: CallRecord;
  isDemoAccount?: boolean;
};

type HistorLoaderProps = {
  count: number;
};

type RenderOptionsParams = {
  allFields: CallEditableFields;
  analysisType: AnalysisTypeEnum;
  isOriginal: boolean;
  value: any;
};

type RestoreConfig = {
  isRestoreOpen: boolean;
  analysisType: AnalysisTypeEnum | null;
};

type EditHistoryItem<FieldValueType = any> = {
  analysisType: AnalysisTypeEnum;
  canRestore: boolean;
  editedBy: string;
  editedDate: string;
  fieldLabel: string;
  originalValue: FieldValueType;
  OriginalOption: React.ReactNode;
  recentValue: FieldValueType;
  RecentionOption: React.ReactNode;
};

const HistoryLoader = ({ count }: HistorLoaderProps) => {
  const loadersToShow = Math.min(count, 3);

  return (
    <>
      {Array.from({ length: loadersToShow }).map((_, idx) => (
        <SkeletonLoaders.Loader key={idx} height={theme.spacing(12)} />
      ))}
    </>
  );
};

export const EditHistoryModal: React.FC<EditHistoryModalProps> = ({
  call,
  isDemoAccount,
  isFailedCall,
  isPoorAudio,
  isSkippedCall,
  modalProps,
  onClose,
  updateEditedCallInList,
}) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const { getOptions } = useOptionsProvider();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isServiceQualityEnabled = getFeatureFlagValue('enable-service-quality');

  const hasAclAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_EDIT_AI_OUTPUT]);
  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const [demoRestoredAnalysis, setDemoRestoredAnalysis] = useState<AnalysisTypeEnum[]>([]);

  const [restoreConfig, setRestoreConfig] = useState<RestoreConfig>({
    isRestoreOpen: false,
    analysisType: null,
  });

  const [pendingUpdateCallInList, setPendingUpdateCallInList] = useState<null | Partial<CallRecord>>(null);

  const isOnlyPoorAudio = isPoorAudio && !isSkippedCall && !isFailedCall;

  const demoCallHistory = useMemo(() => call && getMockCallEditHistory(call), [call?.id]);
  const demoCallRestoredHistory = useMemo(() => {
    const allRestoredAnalysis = Array.from(
      new Set([...demoRestoredAnalysis, restoreConfig.analysisType as AnalysisTypeEnum])
    );
    setDemoRestoredAnalysis(allRestoredAnalysis);
    return call && getMockCallEditHistory(call, allRestoredAnalysis);
  }, [call, restoreConfig.analysisType]);

  const { data, isLoading, refetch } = useGetCallEditHistory({
    demoData: restoreConfig.analysisType ? demoCallRestoredHistory : demoCallHistory,
    isDemoAccount: !!isDemoAccount,
    payload: { id: call?.id as string, locationId: call?.locationId as string }, // Type casting is temporary — to be removed after migrating to schema-gen types.
    realQueryOptions: {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
      onError: () => alert.error(t('Failed to fetch call edit history')),
    },
  });

  const { mutate: restoreApi } = useEditAiOutput({
    onSuccess: (_, payload) => {
      const editedFields = mapEditableFieldsToCall(payload.editedFields as CallEditableFields);
      const originalFields = mapEditableFieldsToCall(payload.currentFields as CallEditableFields);
      const changedValues = diffValues(originalFields, editedFields);
      setPendingUpdateCallInList(changedValues);
      refetch();
      alert.success(t('Successfully restored original analysis!'));
    },
    onError: (err) => {
      console.error(err);
      if (http.isHttpError(err)) {
        const errorMessage =
          ((err.data as Record<string, any>)?.code as number) !== 3
            ? t('Unable to restore analysis. Please try again.')
            : t('Updates were made by another user. Please refresh and try again.');
        alert.error(errorMessage);
      }
    },
    onSettled: () => {
      handleRestoreCancel();
    },
  });

  const renderOptions = ({ allFields, analysisType, isOriginal, value }: RenderOptionsParams) => {
    const fieldMapping = FIELD_MAPPING[analysisType];

    if (!fieldMapping) {
      return <Text size='medium'>{DEFAULT_FIELD_EMPTY_VALUE}</Text>;
    }

    const { emptyValue, optionType } = fieldMapping;
    let options = getOptions(optionType);

    switch (analysisType) {
      case AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY: {
        const resolvedValue = value as CallEditableFields['schedulingOpportunity'];

        return !isNull(resolvedValue) ? (
          resolvedValue ? (
            options[SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED]
          ) : (
            options[SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED]
          )
        ) : (
          <Text size='medium'>{emptyValue}</Text>
        );
      }

      case AnalysisTypeEnum.ANALYSIS_TYPE_CATEGORY: {
        const resolvedValue = value as CallEditableFields['categories'];

        return resolvedValue && !isEmpty(resolvedValue) ? (
          <div css={styles.chipsWrapper}>
            {resolvedValue.map((category) => (
              <div key={category}>{options[category]}</div>
            ))}
          </div>
        ) : (
          <Text size='medium'>{emptyValue}</Text>
        );
      }

      case AnalysisTypeEnum.ANALYSIS_TYPE_SENTIMENT:
      case AnalysisTypeEnum.ANALYSIS_TYPE_CONTACT_TYPE:
        return !isEmpty(value) ? options[value] : <Text size='medium'>{emptyValue}</Text>;

      case AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME: {
        const resolvedValue = value as CallEditableFields['schedulingOutcome'];

        if (isNull(resolvedValue)) return <Text size='medium'>{DEFAULT_FIELD_EMPTY_VALUE}</Text>;

        let schedulingOpportunity = false;

        if (isOriginal) {
          schedulingOpportunity = !!allFields.schedulingOpportunity;
        } else {
          const schedulingOpportunityEdit = data?.current.find(
            (edit) => edit.analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY
          );

          if (schedulingOpportunityEdit === undefined) {
            schedulingOpportunity = !!data?.original.schedulingOpportunity;
          } else {
            schedulingOpportunity = !!schedulingOpportunityEdit?.fields?.schedulingOpportunity;
          }
        }

        if (!schedulingOpportunity) {
          return <Text size='medium'>{emptyValue}</Text>;
        }

        return value
          ? options[SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]
          : options[SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED];
      }

      case AnalysisTypeEnum.ANALYSIS_TYPE_APPOINTMENT_TYPE: {
        const resolvedValue = value as AppointmentTypeEnum[] | null;

        const extraAppointmentTypes = isOriginal
          ? allFields.appointmentTypes?.appointmentType ?? []
          : data?.current.find((edit) => edit.analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_APPOINTMENT_TYPE)?.fields
              .appointmentTypes?.appointmentType ?? [];

        options = getOptions(optionType, 'record', {
          extraAppointmentTypes,
        });

        return resolvedValue && !isEmpty(resolvedValue) ? (
          <div css={styles.chipsWrapper}>
            {resolvedValue.map((appointmentType) => (
              <div key={appointmentType}>{options[appointmentType]}</div>
            ))}
          </div>
        ) : (
          <Text size='medium'>{emptyValue}</Text>
        );
      }

      case AnalysisTypeEnum.ANALYSIS_TYPE_SERVICE_QUALITY_V2: {
        const resolvedValue = value as CallEditableFields['serviceQualityFlags'];

        return resolvedValue && !isEmpty(resolvedValue) ? (
          <div css={styles.chipsWrapper}>
            {resolvedValue.map((flags) => (
              <div key={flags.serviceQualityFlag}>{options[flags.serviceQualityFlag]}</div>
            ))}
          </div>
        ) : (
          <Text size='medium'>{emptyValue}</Text>
        );
      }

      case AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME_V2: {
        const resolvedValue = value as CallEditableFields['schedulingOutcomeV2'];

        const outcomesToHide = [
          SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NOT_APPLICABLE,
          SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN,
        ];

        const isRecoveredOpportunity = resolvedValue === SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL;

        const isUnscheduled = resolvedValue === SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED;

        const recoveredOpportunityOption = getOptions('schedulingOutcomeV2', 'filterRecord', {
          showDisabledChipForRecoveredOpportunity: true,
        });

        const isOriginalRecoveredOpportunity =
          data?.original.schedulingOutcomeV2 === SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL;

        return resolvedValue && !outcomesToHide.includes(resolvedValue) ? (
          <Text size='medium'>
            {!isRecoveredOpportunity ? (
              <>
                <SchedulingChip schedulingOutcome={resolvedValue} showTooltip={false} />
                {isOriginalRecoveredOpportunity && isUnscheduled && (
                  <Text size='small' color='subdued' css={styles.schedulingOutcomeRestoredText}>
                    {t('Removed update made by Call Intelligence')}
                  </Text>
                )}
              </>
            ) : (
              recoveredOpportunityOption[SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]
            )}
          </Text>
        ) : (
          <Text size='medium'>{emptyValue}</Text>
        );
      }
    }

    return null;
  };

  const editHistoryItems = useMemo(() => {
    if (!data) return [];
    const { original, current } = data;

    return current.reduce<EditHistoryItem[]>((acc, edit) => {
      const { analysisType, fields, editedBy, editedAt } = edit;

      if (!FIELD_MAPPING[analysisType]) return acc;

      // Skip service quality analysis if feature flag or access is off
      if (
        analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SERVICE_QUALITY_V2 &&
        !(isServiceQualityEnabled && hasServiceQualityAccess)
      ) {
        return acc;
      }

      // Skip older scheduling analysis if feature flag is off
      if (
        [
          AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME,
          AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY,
        ].includes(analysisType)
      ) {
        return acc;
      } else if (analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME_V2) {
        return acc;
      }

      const { type, label } = FIELD_MAPPING[analysisType];

      type FieldValueType = CallEditableFields[typeof type];

      // Default values
      let originalValue: FieldValueType = original[type];
      let recentValue: FieldValueType = fields[type];
      let canRestore = JSON.stringify(originalValue) !== JSON.stringify(recentValue);

      if (analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_APPOINTMENT_TYPE) {
        originalValue = (original.appointmentTypes?.appointmentType ?? original.appointmentTypes) as FieldValueType;
        recentValue = (fields.appointmentTypes?.appointmentType ?? fields.appointmentTypes) as FieldValueType;
      } else if (analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SENTIMENT) {
        originalValue = (original.callSentiment?.sentiment ?? original.callSentiment) as FieldValueType;
        recentValue = (fields.callSentiment?.sentiment ?? fields.callSentiment) as FieldValueType;
      } else if (analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME) {
        const schedulingOpportunityEdit = data?.current.find(
          (edit) => edit.analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY
        );
        const isOpportunityTrue = !!schedulingOpportunityEdit?.fields?.schedulingOpportunity;

        const adjustedOriginalValue = original.schedulingOpportunity ? originalValue : originalValue || 'none';
        const adjustedRecentValue = isOpportunityTrue ? recentValue : recentValue || 'none';

        canRestore = JSON.stringify(adjustedOriginalValue) !== JSON.stringify(adjustedRecentValue);
      }

      acc.push({
        analysisType,
        canRestore,
        editedBy: `${editedBy?.firstName} ${editedBy?.lastName || ''}`.trim(),
        editedDate: dayjs(editedAt).format('MMM D, YYYY, h:mm A'),
        fieldLabel: label,
        originalValue,
        OriginalOption: renderOptions({ allFields: original, value: originalValue, analysisType, isOriginal: true }),
        recentValue,
        RecentionOption: renderOptions({ allFields: fields, value: recentValue, analysisType, isOriginal: false }),
      });
      return acc;
    }, []);
  }, [data, isServiceQualityEnabled, hasServiceQualityAccess]);

  const handleRestore = (type: AnalysisTypeEnum) => {
    setRestoreConfig({
      isRestoreOpen: true,
      analysisType: type,
    });
  };

  const handleRestoreCancel = () => {
    setRestoreConfig({ isRestoreOpen: false, analysisType: null });
  };

  const handleRestoreOriginal = () => {
    const user = getUser();
    const selectedAnalysisType = restoreConfig.analysisType;

    const editableField = FIELD_MAPPING[selectedAnalysisType as AnalysisTypeEnum]?.type;

    if (!editableField) return;

    const originalAnalysisValue = data?.original[editableField];
    const mostRecentEdit = data?.current.find((edit) => edit.analysisType === selectedAnalysisType);

    // Add additional logic for dependent analysis types
    let additionalEditedFields = {};
    let additionalCurrentFields = {};

    if (selectedAnalysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY) {
      additionalEditedFields = {
        schedulingOutcome: data?.original['schedulingOutcome'],
      };

      const mostRecentEdit = data?.current.find(
        (edit) => edit.analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME
      );

      additionalCurrentFields = {
        schedulingOutcome: mostRecentEdit?.fields?.schedulingOutcome,
      };
    } else if (selectedAnalysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME) {
      additionalEditedFields = {
        schedulingOpportunity: data?.original['schedulingOpportunity'],
      };

      const mostRecentEdit = data?.current.find(
        (edit) => edit.analysisType === AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY
      );

      additionalCurrentFields = {
        ...(mostRecentEdit && { schedulingOpportunity: mostRecentEdit?.fields?.schedulingOpportunity }),
      };
    }

    restoreApi({
      id: call?.id as string,
      locationId: call?.locationId as string,
      userId: user?.userID as string,
      currentFields: { [editableField]: mostRecentEdit?.fields?.[editableField], ...additionalCurrentFields },
      editedFields: {
        [editableField]: originalAnalysisValue,
        ...additionalEditedFields,
      },
      _otherOptions: {
        type: 'restore',
      },
    });
  };

  useEffect(() => {
    if (pendingUpdateCallInList) {
      updateEditedCallInList(pendingUpdateCallInList, true);
      setPendingUpdateCallInList(null);
    }
    //due to infinite loop issue with useEffect, we are not using updateEditedCallInList directly in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUpdateCallInList]);

  if (restoreConfig.isRestoreOpen) {
    const { analysisType } = restoreConfig;

    if (!(analysisType && FIELD_MAPPING[analysisType])) return null;

    return (
      <RestoreHistoryModal
        analysisType={FIELD_MAPPING[analysisType]?.label}
        dependentAnalysis={FIELD_MAPPING[analysisType]?.dependentAnalysis.map(
          (analysis) => FIELD_MAPPING[analysis]!.label
        )}
        modalProps={modalProps}
        onClose={handleRestoreCancel}
        onRestore={handleRestoreOriginal}
        isDemoAccount={isDemoAccount}
      />
    );
  }

  return (
    <Modal {...modalProps} maxWidth={600} css={styles.modalWrapper} onClose={onClose}>
      <Modal.Header onClose={onClose}>{t('Call Insights Edit History')}</Modal.Header>
      <Modal.Body css={styles.body}>
        <div css={styles.callerInfo}>
          <ContactUserName
            callDirection={call.direction === CallDirectionEnum.DIRECTION_OUTBOUND ? 'outbound' : 'inbound'}
            contact={{
              ...callIntelUtils.getContactInfo(call),
              defaultName: t('Unknown'),
            }}
            locationId={call.locationId ?? ''}
          />
          <Text>{dayjs(call?.startTime).format('MMM D, YYYY, h:mm A')}</Text>
        </div>

        {isOnlyPoorAudio && (
          <CallAnalyzeStatus
            css={css`
              margin-top: ${theme.spacing(-2)};
              margin-bottom: ${theme.spacing(0)};
            `}
            isFailedCall={isFailedCall}
            isPoorAudio={isPoorAudio}
            isSkippedCall={isSkippedCall}
            type='editHistory'
          />
        )}

        {isLoading ? (
          <HistoryLoader count={3} />
        ) : (
          editHistoryItems.map((item) => (
            <div key={item.analysisType} css={styles.historyItemWrapper}>
              <div css={styles.historyHeadingWrapper}>
                <Text weight='bold'>{item.fieldLabel}</Text>
                <Button
                  variant='tertiary'
                  destructive
                  iconName='send-back'
                  disabled={!hasAclAccess || !item.canRestore}
                  onClick={() => handleRestore(item.analysisType)}
                  trackingId={generateTrackingId({
                    component: 'edit-history',
                    context: 'restore-btn-click',
                    isDemoAccount,
                  })}
                  data-testid='call-analysis-restore-analysis-btn'
                >
                  {t('Restore')}
                </Button>
              </div>
              <div css={styles.editHistoryGrid}>
                <div css={styles.gridRow}>
                  {/* Original Analysis */}
                  <div css={styles.gridColumn}>
                    <Text color='subdued' size='medium'>
                      {t('AI Analysis')}
                    </Text>
                    <div css={styles.gridValueCell}>{item.OriginalOption}</div>
                  </div>

                  <div css={styles.gridColumn}>
                    {/* Most Recent Edit */}
                    <Text color='subdued' size='medium'>
                      {t('Most Recent Change')}
                    </Text>
                    <div css={styles.gridValueCell}>
                      {item.RecentionOption}
                      {!item.canRestore && (
                        <Text size='small' color='subdued' css={styles.restoredText}>
                          {t('Restored')}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div css={styles.gridColumn}>
                    {/* Edit Details */}
                    <Text color='subdued' size='medium'>
                      {t('Details')}
                    </Text>
                    <div css={styles.gridValueCell}>
                      <Text size='medium'>{item.editedBy} </Text>
                      <Text
                        size='medium'
                        css={css`
                          white-space: normal;
                        `}
                      >
                        {dayjs(item.editedDate).format('MMM D, YYYY, h:mm A')}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </Modal.Body>
    </Modal>
  );
};

const styles = {
  modalWrapper: css`
    width: 100%;
    @media screen and (min-width: 648px) {
      width: 100%;
    }
  `,
  body: css`
    gap: ${theme.spacing(3)};
  `,
  callerInfo: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(2, 0)};
    border-top: 1px solid ${theme.colors.neutral20};
    border-bottom: 1px solid ${theme.colors.neutral20};

    @media screen and (max-width: ${breakpoints.small.min}px) {
      align-items: flex-start;
      flex-direction: column;
      gap: ${theme.spacing(1)};
    }
  `,
  historyItemWrapper: css`
    padding: ${theme.spacing(1.5, 2)};
    display: flex;
    gap: ${theme.spacing(1)};
    flex-direction: column;
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
  `,
  historyHeadingWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  editHistoryGrid: css`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: ${theme.spacing(2)};
    row-gap: ${theme.spacing(1)};

    @media screen and (max-width: ${breakpoints.small.min}px) {
      grid-template-columns: 1fr;
    }
  `,
  gridColumn: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  gridRow: css`
    display: contents; /* Allows grid items to span rows */
    align-items: center;

    @media screen and (max-width: ${breakpoints.small.min}px) {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      gap: ${theme.spacing(2)};
    }
  `,
  schedulingOutcomeRestoredText: css`
    margin-top: ${theme.spacing(1)};
    font-style: italic;
    white-space: break-spaces;
  `,
  gridValueCell: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  chipsWrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
  `,
  restoredText: css`
    margin-top: ${theme.spacing(0.5)};
    font-style: italic;
  `,
};
